/**
 * Shared Bazaarvoice inline-rating hydration helpers.
 *
 * BV scans the DOM on load and hydrates `[data-bv-show="inline_rating"]`
 * placeholders. After AJAX / morph updates, those nodes are often missed —
 * schedule a two-pass recovery, then replace any still-empty node so BV
 * re-scans it.
 *
 * When BV never fills a placeholder (staging mismatch, or a product with no
 * reviews), a settle watchdog waits a short budget then marks the node so CSS
 * can collapse the reserved shimmer instead of leaving it animating forever.
 */

const SETTLE_BUDGET_MS = 4000;
const SETTLED_ATTR = 'data-bv-settled';

/** @type {WeakSet<HTMLElement>} */
const scheduledNodes = new WeakSet();

let settleWatchdogStarted = false;
/** True while waiting for DOMContentLoaded to run boot (avoids double-schedule). */
let settleBootScheduled = false;

/**
 * @param {ParentNode} [root]
 * @returns {HTMLElement[]}
 */
export function getInlineRatingNodes(root = document) {
  return Array.from(root.querySelectorAll('[data-bv-show="inline_rating"][data-bv-product-id]')).filter(
    (node) => node instanceof HTMLElement
  );
}

/**
 * Replace a BV placeholder so BV re-scans it.
 * @param {HTMLElement} node
 * @returns {HTMLElement}
 */
export function replaceInlineRatingNode(node) {
  const replacement = document.createElement('div');
  Array.from(node.attributes).forEach((attribute) => {
    if (attribute.name === SETTLED_ATTR) return;
    replacement.setAttribute(attribute.name, attribute.value);
  });
  node.replaceWith(replacement);
  return replacement;
}

/**
 * @param {HTMLElement} node
 * @returns {boolean}
 */
function isRatingFilled(node) {
  return node.hasAttribute('data-bv-ready') || node.childElementCount > 0 || node.textContent.trim().length > 0;
}

/**
 * Collapse a placeholder (and its rating wrapper) when BV will not fill it.
 * @param {HTMLElement} node
 * @returns {void}
 */
function settleInlineRatingNode(node) {
  if (!(node instanceof HTMLElement) || isRatingFilled(node)) return;

  node.setAttribute(SETTLED_ATTR, '');
  const parent = node.parentElement;
  if (parent instanceof HTMLElement) {
    parent.setAttribute(SETTLED_ATTR, '');
  }
}

/**
 * @param {HTMLElement} node
 * @returns {void}
 */
function scheduleSettleForNode(node) {
  if (!(node instanceof HTMLElement) || scheduledNodes.has(node) || node.hasAttribute(SETTLED_ATTR)) {
    return;
  }

  scheduledNodes.add(node);

  // Always wait the budget. BV may arrive via content_for_header / GTM after
  // this module boots; settling immediately on a one-shot script-tag check
  // would permanently hide ratings that BV would still hydrate.
  window.setTimeout(() => settleInlineRatingNode(node), SETTLE_BUDGET_MS);
}

/**
 * @param {ParentNode} [root]
 * @returns {void}
 */
function scheduleSettleForRoot(root = document) {
  getInlineRatingNodes(root).forEach((node) => scheduleSettleForNode(node));
}

/**
 * Start the settle watchdog once for the whole page.
 * Safe to call from the global script tag and from facets / predictive-search imports.
 * Must never throw: importers (predictive-search, facets) need this module's exports
 * even when the watchdog cannot attach.
 * @returns {void}
 */
export function startBvInlineRatingSettleWatchdog() {
  if (settleWatchdogStarted || settleBootScheduled) return;

  const boot = () => {
    settleBootScheduled = false;
    try {
      scheduleSettleForRoot(document);

      if (!('MutationObserver' in window) || !document.body) {
        settleWatchdogStarted = true;
        return;
      }

      const observer = new MutationObserver((mutations) => {
        try {
          for (const mutation of mutations) {
            mutation.addedNodes.forEach((added) => {
              if (!(added instanceof Element)) return;

              if (
                added instanceof HTMLElement &&
                added.matches('[data-bv-show="inline_rating"][data-bv-product-id]')
              ) {
                scheduleSettleForNode(added);
              }

              if (added.querySelector?.('[data-bv-show="inline_rating"][data-bv-product-id]')) {
                scheduleSettleForRoot(added);
              }
            });
          }
        } catch (error) {
          console.error('[bv-inline-ratings] settle observer failed', error);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
      settleWatchdogStarted = true;
    } catch (error) {
      // Leave flags false so a later import/call can retry.
      console.error('[bv-inline-ratings] settle watchdog failed to start', error);
    }
  };

  if (document.readyState === 'loading') {
    settleBootScheduled = true;
    document.addEventListener('DOMContentLoaded', boot, { once: true });
    return;
  }

  boot();
}

/**
 * Schedule recovery for nodes BV missed after an AJAX/morph update.
 * Keep the run-id guard so a superseded render cannot clobber a newer one.
 *
 * @param {object} options
 * @param {number} options.runId - Run id captured when this recovery was scheduled
 * @param {() => number} options.getCurrentRunId - Returns the latest run id for this surface
 * @param {ParentNode} [options.root] - Scope for querying rating nodes (default document)
 * @returns {void}
 */
export function scheduleBvMissedHydrationRecovery({ runId, getCurrentRunId, root = document }) {
  requestAnimationFrame(() => {
    window.setTimeout(() => recoverMissedInlineRatings(runId, 0, getCurrentRunId, root), 300);
  });
}

/**
 * @param {number} runId
 * @param {number} attempt
 * @param {() => number} getCurrentRunId
 * @param {ParentNode} root
 * @returns {void}
 */
function recoverMissedInlineRatings(runId, attempt, getCurrentRunId, root) {
  if (runId !== getCurrentRunId()) return;

  const missedNodes = getInlineRatingNodes(root).filter(
    (node) => !node.hasAttribute('data-bv-ready') && !node.hasAttribute(SETTLED_ATTR)
  );
  if (missedNodes.length === 0) return;

  // Give BV a second pass before replacing nodes.
  if (attempt === 0) {
    window.setTimeout(() => recoverMissedInlineRatings(runId, 1, getCurrentRunId, root), 800);
    return;
  }

  // Last-step recovery: replace only nodes BV still has not hydrated.
  missedNodes.forEach((node) => replaceInlineRatingNode(node));
}

// Self-boot for the global script tag. Wrapped so a boot failure cannot fail
// module evaluation and break static importers (predictive-search / facets).
try {
  startBvInlineRatingSettleWatchdog();
} catch (error) {
  console.error('[bv-inline-ratings] settle watchdog self-boot failed', error);
}
